import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

/**
 * A soft glowing outline round the things the task on screen needs, and a
 * brighter one round the thing the prompt is about, so it is never a guess
 * which of the things in front of you the button means.
 *
 * Drawing only, like the rest of `world/`: it is handed plain boxes each
 * frame (`{ x, y, z, hx, hy, hz, person, strong }`, metres, `y` the bottom)
 * and draws a shell a little larger than each, lit at its edges: a rim term
 * on a rounded box or a capsule, added over the scene, so it reads as a glow
 * round the thing's outline and leaves the thing itself as it was. No
 * collider, nothing the simulation reads, and the same in the greybox.
 *
 * Its own meshes rather than the things' own: loose things are drawn from
 * pools rebuilt every frame and the level's furniture is batched by material,
 * so there is no one mesh per thing to outline. A shell round the thing's
 * box is the same for a chair, a shelf, a cart and a person.
 */

const MAX = 16;
/** How far the shell stands off the thing's box, metres. */
const PAD = 0.07;

const vertexShader = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    vec4 view = modelViewMatrix * vec4(position, 1.0);
    vView = -view.xyz;
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * view;
  }`;

const fragmentShader = /* glsl */ `
  uniform vec3 colour;
  uniform float strength;
  uniform float time;
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    float facing = abs(dot(normalize(vNormal), normalize(vView)));
    float rim = pow(1.0 - facing, 2.6);
    float pulse = 0.78 + 0.22 * sin(time * 3.2);
    gl_FragColor = vec4(colour * rim * strength * pulse, 1.0);
  }`;

function glow(colour, strength) {
  return new THREE.ShaderMaterial({
    uniforms: { colour: { value: new THREE.Color(colour) }, strength: { value: strength }, time: { value: 0 } },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
}

export function createHighlightView() {
  const group = new THREE.Group();
  group.name = 'highlights';
  const box = new RoundedBoxGeometry(1, 1, 1, 3, 0.18);
  const capsule = new THREE.CapsuleGeometry(0.5, 1, 4, 14);
  const soft = glow(0xe8b860, 0.85);
  const strong = glow(0xfff1c8, 1.9);
  const pool = [];
  for (let i = 0; i < MAX; i += 1) {
    const mesh = new THREE.Mesh(box, soft);
    mesh.visible = false;
    mesh.frustumCulled = true;
    mesh.renderOrder = 5;
    group.add(mesh);
    pool.push(mesh);
  }
  let time = 0;

  return {
    object3D: group,

    /** The boxes to outline this frame; anything past `MAX` is not drawn. */
    set(list) {
      for (let i = 0; i < MAX; i += 1) {
        const mesh = pool[i];
        const item = list[i];
        if (!item) {
          mesh.visible = false;
          continue;
        }
        mesh.visible = true;
        mesh.geometry = item.person ? capsule : box;
        mesh.material = item.strong ? strong : soft;
        const hy = item.hy + PAD;
        mesh.position.set(item.x, item.y + item.hy, item.z);
        if (item.person) {
          // The capsule is 2 tall and 1 across at unit scale.
          mesh.scale.set((item.hx + PAD) * 2, hy, (item.hz + PAD) * 2);
        } else {
          mesh.scale.set((item.hx + PAD) * 2, hy * 2, (item.hz + PAD) * 2);
        }
      }
    },

    update(dt) {
      time += dt;
      soft.uniforms.time.value = time;
      strong.uniforms.time.value = time * 1.4;
    },

    get count() {
      return pool.filter((mesh) => mesh.visible).length;
    },
  };
}
